/**
 * Utilities for formatting crypto values and parsing tickers.
 *
 * These helpers avoid floating point rounding surprises by operating primarily on strings.
 */

/**
 * Parse a blockchain ticker like "ETH_USDT" or "BSC_BUSD".
 * - Single-part (e.g., "ETH", "BNB") -> { blockchain: "ETH", token: undefined }
 * - Two-part  (e.g., "ETH_USDT")     -> { blockchain: "ETH", token: "USDT" }
 */
export function splitTicker(ticker: string): {
	blockchain: string;
	token?: string;
	isToken: boolean;
} {
	const parts = (ticker || "").trim().toUpperCase().split("_").filter(Boolean);

	if (parts.length === 0) {
		return { blockchain: "", isToken: false };
	}

	if (parts.length === 1) {
		// Examples: ETH, TRON, MATIC, BNB
		return { blockchain: parts[0], token: undefined, isToken: false };
	}

	// Examples: ETH_USDT, MATIC_USDC, BSC_BUSD
	return { blockchain: parts[0], token: parts[1], isToken: true };
}

/**
 * Check whether a token symbol is a known stablecoin (used to set sensible default decimals).
 */
export function isStableToken(symbol?: string): boolean {
	if (!symbol) return false;
	const s = symbol.toUpperCase();
	return s === "USDT" || s === "USDC" || s === "BUSD";
}

/**
 * Remove trailing zeros from a decimal string, keeping at least `minDecimals` digits when provided.
 */
export function trimTrailingZeros(value: string, minDecimals = 0): string {
	if (!value.includes(".")) return value;

	const [intPart, fracPartRaw] = value.split(".");
	const fracPart = fracPartRaw.replace(/0+$/, "");

	if (fracPart.length === 0) {
		return minDecimals > 0 ? `${intPart}.${"0".repeat(minDecimals)}` : intPart;
	}

	if (fracPart.length < minDecimals) {
		return `${intPart}.${fracPart}${"0".repeat(minDecimals - fracPart.length)}`;
	}

	return `${intPart}.${fracPart}`;
}

/**
 * Round UP a decimal string to at most `maxDecimals` fractional digits.
 * This ensures the displayed amount is always sufficient for payment (never rounds down).
 */
export function roundUpDecimals(value: string, maxDecimals: number): string {
	if (maxDecimals < 0) return value;
	const num = Number(value);
	if (!Number.isFinite(num)) return value;

	const multiplier = 10 ** maxDecimals;
	const rounded = Math.ceil(num * multiplier) / multiplier;
	return rounded.toFixed(maxDecimals);
}

/**
 * Add thousand separators to the integer part of a decimal string.
 */
export function addThousandSeparators(value: string, locale = "en-US"): string {
	if (!value) return value;
	const negative = value.startsWith("-");
	const v = negative ? value.slice(1) : value;

	const [intPartRaw, fracPart] = v.split(".");
	const intPart = Number.isNaN(Number(intPartRaw))
		? intPartRaw
		: Number(intPartRaw).toLocaleString(locale);

	const withFrac = fracPart !== undefined ? `${intPart}.${fracPart}` : intPart;
	return negative ? `-${withFrac}` : withFrac;
}

/**
 * Normalize any numeric input (number or string) into a plain decimal string.
 * - Strips spaces
 * - Converts scientific notation to fixed string when possible
 */
export function toPlainNumberString(input: string | number): string {
	if (input === null || input === undefined) return "0";
	if (typeof input === "number") {
		if (!Number.isFinite(input)) return "0";
		// Avoid scientific notation for small/large numbers
		const asStr = input.toString();
		if (/e/i.test(asStr)) {
			// Choose a safe precision for display purposes
			// (12 is a pragmatic default to balance precision vs. noise)
			return input.toFixed(12).replace(/\.?0+$/, "");
		}
		return asStr;
	}

	// String path
	const s = input.trim();
	if (!s) return "0";

	if (/e/i.test(s)) {
		const n = Number(s);
		if (Number.isFinite(n)) {
			return toPlainNumberString(n); // delegate to number branch
		}
		return "0";
	}

	// Validate basic numeric form
	const match = s.match(/^-?\d+(\.\d+)?$/);
	if (!match) return "0";
	return s;
}

export type FormatCryptoOptions = {
	ticker?: string;
	/**
	 * Max decimals to keep (rounded). If not provided:
	 * - 8 for known stables (USDT/USDC/BUSD)
	 * - 8 default for others
	 */
	maxDecimals?: number;
	/**
	 * Minimum decimals to keep (after trimming zeros). Defaults to 0.
	 */
	minDecimals?: number;
	/**
	 * Whether to add thousand separators to integer part. Defaults to true.
	 */
	useGrouping?: boolean;
	/**
	 * Locale for grouping (e.g., "en-US"). Defaults to "en-US".
	 */
	locale?: string;
};

/**
 * Format a crypto amount for display: rounding, optional grouping, and zero trimming.
 *
 * Examples:
 * - formatCryptoAmount("9.994107574115378423", { ticker: "BSC_USDT" }) -> "9.99410757"
 * - formatCryptoAmount("1000", { useGrouping: true }) -> "1,000"
 * - formatCryptoAmount("0.1000000", { minDecimals: 2 }) -> "0.10"
 */
export function formatCryptoAmount(
	amount: string | number,
	options: FormatCryptoOptions = {},
): string {
	const {
		ticker,
		maxDecimals,
		minDecimals = 0,
		useGrouping = true,
		locale = "en-US",
	} = options;

	const plain = toPlainNumberString(amount);

	// Determine sensible decimals if not explicitly provided
	let decimalsToKeep =
		typeof maxDecimals === "number" ? maxDecimals : undefined;

	if (decimalsToKeep === undefined) {
		const { token } = splitTicker(ticker || "");
		decimalsToKeep = isStableToken(token) ? 8 : 8;
	}

	// Round UP to specified decimals (ensures payment amount is sufficient)
	const rounded = roundUpDecimals(plain, decimalsToKeep);

	// Trim trailing zeros but preserve minDecimals if requested
	const trimmed = trimTrailingZeros(rounded, minDecimals);

	// Group integer part for readability
	return useGrouping ? addThousandSeparators(trimmed, locale) : trimmed;
}

/**
 * Shorten a blockchain address, keeping the start and end.
 * Example: 0x488A...520dA
 */
export function formatAddressShort(
	address: string,
	opts: { head?: number; tail?: number } = {},
): string {
	const head = opts.head ?? 6;
	const tail = opts.tail ?? 5;
	if (!address) return "";
	if (address.length <= head + tail + 3) return address;
	return `${address.slice(0, head)}...${address.slice(-tail)}`;
}
